/**
 * @file: useTakePhoto.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { useAppNavigation } from '@euler/app/components/AppNavigationProvider';
import { CameraViewMaskType } from '@euler/app/components/media/camera/CameraView';
import { MediaResultEx } from '@euler/app/flows/media/types';
import { usePersistFn } from '@euler/utils/hooks';
import {
  ImageResult,
  manipulateAsync,
  SaveFormat,
} from 'expo-image-manipulator';
import { CameraPosition } from 'react-native-vision-camera';

export const useTakePhoto = ({
  mask,
  initialPosition,
  quality = 80,
  pixelScale = 2,
  defaultMaxWidth = 600,
  defaultMaxHeight = 600,
}: {
  quality?: number;
  initialPosition?: CameraPosition;
  defaultMaxWidth?: number;
  defaultMaxHeight?: number;
  pixelScale?: number;
  mask?: CameraViewMaskType | null;
} = {}) => {
  const navigation = useAppNavigation();
  return usePersistFn(
    (
      callback: (result: ImageResult, originalResult: MediaResultEx) => void,
    ) => {
      navigation.navigate('_camera', {
        photoOnly: true,
        mask,
        initialPosition,
        onResult: async result => {
          // Fix: no need to go back on web after captured
          // Platform.OS !== 'web' && navigation.goBack();
          // Fix: page has no reaction after captured on web
          navigation.goBack();

          if (result.type !== 'photo') return;

          const width = result?.mask?.width || defaultMaxWidth;
          const height = result?.mask?.height || defaultMaxHeight;

          const res = await manipulateAsync(
            result.uri,
            [
              {
                resize: {
                  //Fix: web result has no size
                  width: width * pixelScale,
                  height: height * pixelScale,
                },
              },
            ],
            {
              compress: quality / 100,
              format: SaveFormat.JPEG,
            },
          );

          callback(res, result);
        },
      });
    },
  );
};
